import React, { useEffect, useState } from 'react';
import { usePortalStore } from '../../store';
import { TrendingUp, Clock } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

interface LogEntry { entry_type: string; skill_name: string | null; epic_id: string | null; created_at: string; }
interface QualityTrend { pass_rate: number; pass_count: number; fail_count: number; pending_count: number; total: number; }

export const CompanyOverview: React.FC = () => {
  const { company, departments, gates, navigateTo, waveStatus } = usePortalStore();
  const [recentActivity, setRecentActivity] = useState<LogEntry[]>([]);
  const [qualityTrend, setQualityTrend] = useState<QualityTrend | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/logbook?limit=5`)
      .then(r => r.ok ? r.json() : [])
      .then((data: LogEntry[]) => setRecentActivity(data))
      .catch(() => {});
    fetch(`${API_BASE}/api/zpc/quality-trend`)
      .then(r => r.ok ? r.json() : null)
      .then((data: QualityTrend | null) => setQualityTrend(data))
      .catch(() => {});
  }, []);

  if (!company) {
    return <div className="text-slate-400">Loading company data...</div>;
  }

  const pendingGates = gates.filter(g => g.status === 'pending');
  const deliveredCount = waveStatus?.number ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-100 mb-2">{company.name}</h1>
        <p className="text-slate-400">{company.mission}</p>
      </div>

      {/* Board Dashboard */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4">Board Dashboard</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Health */}
          <div className="bg-slate-900 rounded-lg p-4">
            <div className="text-xs text-slate-400 mb-2">Health</div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden mb-2">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500"
                style={{ width: `${company.health}%` }}
              />
            </div>
            <div className="text-xl font-bold text-slate-100">{company.health}%</div>
          </div>

          {/* Cost/Week */}
          <div className="bg-slate-900 rounded-lg p-4">
            <div className="text-xs text-slate-400 mb-2">Cost/Week</div>
            <div className="text-xl font-bold text-amber-400">${(company.costPerWeek / 1000).toFixed(1)}k</div>
          </div>

          {/* Pending Gates */}
          <div className="bg-slate-900 rounded-lg p-4">
            <div className="text-xs text-slate-400 mb-2">Pending Gates</div>
            <div className="text-xl font-bold text-red-400">{pendingGates.length}</div>
            {pendingGates.length > 0 && (
              <button
                onClick={() => navigateTo('gates')}
                className="mt-2 text-xs text-cyan-400 hover:text-cyan-300"
              >
                Review →
              </button>
            )}
          </div>

          {/* Delivered */}
          <div className="bg-slate-900 rounded-lg p-4">
            <div className="text-xs text-slate-400 mb-2">Delivered</div>
            <div className="text-xl font-bold text-cyan-400">{deliveredCount}</div>
            <TrendingUp className="w-4 h-4 text-emerald-500 mt-1" />
          </div>
        </div>

        {/* Quality Trend */}
        <div className="mt-4 bg-slate-900 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-400">Quality Pass Rate</span>
            {qualityTrend ? (
              <span className={`text-sm font-bold ${qualityTrend.pass_rate >= 80 ? 'text-emerald-400' : qualityTrend.pass_rate >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                {qualityTrend.pass_rate}%
              </span>
            ) : (
              <span className="text-sm text-slate-500">—</span>
            )}
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                !qualityTrend ? 'bg-slate-600' :
                qualityTrend.pass_rate >= 80 ? 'bg-emerald-500' :
                qualityTrend.pass_rate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: qualityTrend ? `${qualityTrend.pass_rate}%` : '0%' }}
            />
          </div>
          {qualityTrend && (
            <div className="flex gap-4 mt-2">
              <span className="text-xs text-emerald-400">{qualityTrend.pass_count} pass</span>
              <span className="text-xs text-red-400">{qualityTrend.fail_count} fail</span>
              <span className="text-xs text-yellow-400">{qualityTrend.pending_count} pending</span>
            </div>
          )}
        </div>
      </div>

      {/* Departments Grid */}
      <div>
        <h2 className="text-lg font-semibold text-slate-100 mb-4">Departments</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {departments.map(dept => (
            <div
              key={dept.id}
              onClick={() => navigateTo('department')}
              className="bg-slate-800 border border-slate-700 rounded-lg p-6 hover:border-slate-600 hover:bg-slate-800/50 transition-colors cursor-pointer"
            >
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-slate-100">{dept.name}</h3>
                <p className="text-xs text-slate-400 mt-1">{dept.charter}</p>
              </div>

              <div className="space-y-3">
                {/* Skills */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Skills</span>
                  <span className="text-sm font-semibold text-slate-200">{dept.skillCount}</span>
                </div>

                {/* Queue */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-slate-400">Queue</span>
                    <span className="text-sm font-semibold text-slate-200">{dept.queueDepth}</span>
                  </div>
                  <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${
                        dept.queueDepth === 0
                          ? 'bg-emerald-500'
                          : dept.queueDepth < 3
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min((dept.queueDepth / 5) * 100, 100)}%` }}
                    />
                  </div>
                </div>

                {/* Cost */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Cost/Week</span>
                  <span className="text-sm font-semibold text-amber-400">${(dept.costPerWeek / 1000).toFixed(0)}k</span>
                </div>

                {/* Workflows */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Active</span>
                  <span className="text-sm font-semibold text-cyan-400">{dept.workflowCount} workflow{dept.workflowCount !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4">Recent Activity</h2>
        <div className="space-y-3">
          {recentActivity.length === 0 ? (
            <p className="text-sm text-slate-500">No recent activity recorded.</p>
          ) : recentActivity.map((entry, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-cyan-500 shrink-0" />
              <span className="text-sm text-slate-400 shrink-0">{entry.created_at.slice(11, 16)}</span>
              <span className="text-sm text-slate-200">
                {entry.skill_name ?? entry.entry_type}
                {entry.epic_id ? ` — ${entry.epic_id}` : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
